import type { CategoryKind, CategoryRefSource } from '@/types';
import { chartSeries } from '@/lib/theme/tokens';
import {
  type CategoryChipStyle,
  categoryChipStyleFromColor,
  resolveCategoryColor,
} from '@/lib/transactions/categories';

// The picker offers the FULL Phosphor library (IconPicker); the curated
// vocabulary + pt-BR labels live in `@/lib/category-icons.json` and the AI
// suggestion gate uses CATEGORY_ICON_NAMES from `@/components/CategoryIcon`.
export const DEFAULT_CATEGORY_ICON = 'shopping-cart';

/**
 * Seed color for a brand-new custom category: the first chart-series hue.
 * Imperative (DOM token read) so state initializers and effects can call it
 * without hook plumbing; picks stay stored as the resolved hex.
 */
export function defaultCategoryColor(): string {
  return chartSeries()[0];
}

export interface SuggestionDirtyFlags {
  icon: boolean;
  color: boolean;
  kind?: boolean;
}

/**
 * The AI suggestion only fills fields the user hasn't touched — a late response
 * must never overwrite an explicit pick. Icon is re-checked against the preset
 * list client-side (belt-and-braces on top of the server's validation).
 */
export function applySuggestion(
  suggestion: { icon: string; color: string; kind: CategoryKind },
  dirty: SuggestionDirtyFlags,
  iconChoices: readonly string[],
): { icon?: string; color?: string; kind?: CategoryKind } {
  const patch: { icon?: string; color?: string; kind?: CategoryKind } = {};
  if (!dirty.icon && iconChoices.includes(suggestion.icon)) patch.icon = suggestion.icon;
  if (!dirty.color) patch.color = suggestion.color;
  if (dirty.kind === false) patch.kind = suggestion.kind;
  return patch;
}

export type CategoryVisual = CategoryChipStyle;

/**
 * Tile/accent colors for a category ref — sugar over the ONE color system
 * (`resolveCategoryColor` + `categoryChipStyleFromColor`), so a ref renders the
 * same color here, in the detail drawer and on /transactions. Customs use their
 * user-picked color; Pluggy refs (root or leaf) the hierarchical palette.
 */
export function categoryVisual(
  ref: string,
  source: CategoryRefSource,
  customColor?: string | null,
): CategoryVisual {
  return categoryChipStyleFromColor(resolveCategoryColor(ref, source, customColor));
}
